import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { Verdict, VerdictChip } from 'components/common/Verdict/Verdict';
import { ClientFeedbackResolution } from '@constants';
import { ICandidateDetails, ICandidateInterview, IPerson } from '@types';
import { formatDate, geDateTimeInTimezone } from '@utils';

const VERDICT_BY_RESOLUTION: Record<
  ClientFeedbackResolution,
  { verdict: Verdict; label: string }
> = {
  [ClientFeedbackResolution.HiringApproved]: {
    verdict: 'passed',
    label: 'Passed the interview',
  },
  [ClientFeedbackResolution.Rejected]: {
    verdict: 'failed',
    label: 'Failed the interview',
  },
  [ClientFeedbackResolution.CallRequested]: {
    verdict: 'undecided',
    label: 'Another round requested',
  },
};

const ORDINALS = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th'];

const roundDate = (round: ICandidateInterview): string | null => {
  const start = round.scheduledSlot?.[0]?.start;

  if (start)
    return round.clientTimezone
      ? geDateTimeInTimezone(start, round.clientTimezone).format(
          'MMM DD, YYYY, hh:mm a',
        )
      : formatDate(start, 'MMM d, yyyy, hh:mm a');

  return formatDate(round.result?.providedAt) || null;
};

const initials = (person?: IPerson | null): string =>
  (person?.name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

const Reviewer: React.FC<{ person: IPerson }> = ({ person }) => (
  <Box display="flex" alignItems="center" gap={1} minWidth={0}>
    <Box
      aria-hidden="true"
      sx={{
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        width: 24,
        height: 24,
        borderRadius: '50%',
        backgroundColor: 'highlight.neutral',
        color: 'text.secondary',
        fontSize: '0.625rem',
        fontWeight: 600,
      }}
    >
      {initials(person)}
    </Box>
    <Typography variant="body2" noWrap>
      {person.name}
    </Typography>
  </Box>
);

export const InterviewFeedbackList: React.FC<{
  data: ICandidateDetails;
}> = ({ data }) => {
  const allRounds = data.interview ?? [];
  const rounds = allRounds
    .map((round, index) => ({ round, index }))
    .filter(({ round }) => round.result);
  if (!rounds.length) return null;

  return (
    <Stack gap={1.5}>
      {rounds.map(({ round, index }) => {
        const result = round.result!;
        const { verdict, label } = VERDICT_BY_RESOLUTION[result.resolution] ?? {
          verdict: 'undecided' as Verdict,
          label: result.resolution,
        };
        const date = roundDate(round);
        const reasons = result.rejectionReason?.join(', ');

        return (
          <Box
            key={index}
            component="article"
            sx={(theme) => ({
              p: 1.5,
              borderRadius: 1,
              border: `1px solid ${theme.palette.background.divider}`,
              backgroundColor: 'common.white',
            })}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              flexWrap="wrap"
            >
              <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                <Typography variant="body1" fontWeight={500}>
                  {allRounds.length > 1
                    ? `${ORDINALS[index] ?? `${index + 1}th`} interview`
                    : 'Interview'}
                </Typography>
                {Boolean(result.reviewer) && (
                  <Reviewer person={result.reviewer} />
                )}
              </Box>
              {Boolean(date) && (
                <Typography variant="body2" color="text.secondary">
                  {date}
                </Typography>
              )}
            </Box>

            {Boolean(label || reasons) && (
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                flexWrap="wrap"
                mt={1}
              >
                {Boolean(label) && (
                  <VerdictChip verdict={verdict} label={label} />
                )}
                {Boolean(reasons) && (
                  <Typography variant="body2" color="text.secondary">
                    {reasons}
                  </Typography>
                )}
              </Box>
            )}

            {Boolean(result.feedback) && (
              <Typography variant="body1" whiteSpace="pre-line" mt={1}>
                {result.feedback}
              </Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};
